import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import {
  Mail, X, Loader2, Send, Eye, Pencil, CheckCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Coach this call in 1-click" modal.
 *
 * Flow:
 *   1. GET  /coaching-email/preview → default composition (to / subject / html / plain_text)
 *   2. Manager tweaks recipients / subject / body
 *   3. POST /coaching-email/send    → dispatches via SMTP2Go
 *
 * The recipient is always a manager inbox (per product spec — managers
 * forward the note to the agent themselves). Backend hard-codes that.
 */
export const CoachingEmailModal = ({ actionId, token, onClose, onSent }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('preview'); // 'preview' | 'edit'
  const [form, setForm] = useState({
    to: '',
    subject: '',
    html: '',
    plain_text: '',
    agent_name: '',
  });
  const [notCoachable, setNotCoachable] = useState(false);

  // Sanitise the composed HTML once per change — cheap and stable.
  // DOMPurify strips <script>, `on*` handlers, javascript: URLs, etc.
  // Keeps the inline styles used by the composer so the preview still
  // looks right.
  const sanitizedHtml = useMemo(
    () => DOMPurify.sanitize(form.html || '', { ADD_ATTR: ['style'] }),
    [form.html],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/voicefabric/actions/${actionId}/coaching-email/preview`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setForm({
          to: (r.data.to || []).join(', '),
          subject: r.data.subject || '',
          html: r.data.html || '',
          plain_text: r.data.plain_text || '',
          agent_name: r.data.agent_name || '',
        });
        setNotCoachable(!r.data.coachable);
      } catch (err) {
        console.error('coaching-email preview failed', err?.response || err);
        toast.error(
          err?.response?.data?.detail
          || 'Could not compose coaching email — please try again.',
        );
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [actionId, token, onClose]);

  const handleSend = async () => {
    const recipients = form.to.split(',').map((s) => s.trim()).filter((s) => s.includes('@'));
    if (recipients.length === 0) {
      toast.error('Add at least one recipient email address.');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/voicefabric/actions/${actionId}/coaching-email/send`,
        {
          to: recipients,
          subject: form.subject,
          html: form.html,
          plain_text: form.plain_text,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Coaching email sent to ${r.data.sent_to.join(', ')}`);
      onSent?.(r.data);
      onClose();
    } catch (err) {
      console.error('coaching-email send failed', err?.response || err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Send failed — check SMTP settings.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`coaching-email-modal-${actionId}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-teal-500/15 flex items-center justify-center">
              <Mail className="w-4 h-4 text-teal-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Coach this call</h3>
              <p className="text-white/40 text-xs">
                {form.agent_name
                  ? <>Compose a note about <span className="text-teal-300">{form.agent_name}</span>&rsquo;s call, then forward it.</>
                  : 'Compose a coaching note from this call\'s AI insights.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors"
            data-testid="coaching-email-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
          </div>
        ) : notCoachable ? (
          <div className="py-12 px-6 text-center">
            <p className="text-white/60 text-sm">
              This action doesn&rsquo;t contain enough coaching signal (no manager summary,
              coaching fix, or best-next-message). Try a different rule that does grading-style
              analysis.
            </p>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-white/5 space-y-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-14">To</span>
                <input
                  value={form.to}
                  onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
                  placeholder="manager@company.com, cc@company.com"
                  className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-teal-500/50"
                  data-testid="coaching-email-to"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-white/40 w-14">Subject</span>
                <input
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
                  data-testid="coaching-email-subject"
                />
              </div>
            </div>

            <div className="flex items-center gap-1 px-5 pt-3 border-b border-white/5">
              <button
                onClick={() => setTab('preview')}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-t-lg font-medium transition-colors ${
                  tab === 'preview'
                    ? 'bg-white/5 text-teal-300 border-b-2 border-teal-400'
                    : 'text-white/40 hover:text-white/60'
                }`}
                data-testid="coaching-email-tab-preview"
              >
                <Eye className="w-3 h-3" />Preview
              </button>
              <button
                onClick={() => setTab('edit')}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-t-lg font-medium transition-colors ${
                  tab === 'edit'
                    ? 'bg-white/5 text-teal-300 border-b-2 border-teal-400'
                    : 'text-white/40 hover:text-white/60'
                }`}
                data-testid="coaching-email-tab-edit"
              >
                <Pencil className="w-3 h-3" />Edit body
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {tab === 'preview' ? (
                <div
                  className="rounded-lg bg-white p-4 max-h-[52vh] overflow-y-auto"
                  data-testid="coaching-email-preview"
                  // Backend already `html.escape()`'s every dynamic field
                  // and the plain-text editor rebuilds HTML with `<`/`>`/`&`
                  // escaped, so this is safe today. We still run DOMPurify
                  // client-side as defence-in-depth — cheap insurance for
                  // any future field that forgets to escape and a hard
                  // guarantee that <script>/on-handlers can never render.
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              ) : (
                <textarea
                  value={form.plain_text}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    plain_text: e.target.value,
                    // Rebuild HTML from edited plain text so both views stay
                    // aligned. Preserves line breaks and blank paragraph gaps.
                    html: '<div style="font-family:-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif; max-width:640px; padding:24px; white-space:pre-wrap; line-height:1.6; color:#1e293b;">'
                      + e.target.value.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]))
                      + '</div>',
                  }))}
                  rows={22}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono leading-relaxed focus:outline-none focus:border-teal-500/50 resize-y"
                  data-testid="coaching-email-body"
                />
              )}
            </div>

            <div className="flex items-center justify-between px-5 py-3 border-t border-white/10 bg-black/20">
              <p className="text-white/30 text-xs">
                Note goes to the recipient(s) above — they forward it to {form.agent_name || 'the agent'} themselves.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-sm text-white/60 hover:text-white transition-colors"
                  data-testid="coaching-email-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-lg disabled:opacity-50 transition-colors"
                  data-testid="coaching-email-send"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Sending…' : 'Send coaching email'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Small "Sent" badge shown in the trace once an action has been coached.
 * Prevents accidental duplicate sends and gives managers a receipt.
 */
export const CoachingSentBadge = ({ at, to }) => {
  const when = at ? new Date(at).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const recipients = Array.isArray(to) ? to.join(', ') : (to || '');
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 font-semibold"
      title={recipients ? `Sent to ${recipients} at ${when}` : `Sent at ${when}`}
      data-testid="coaching-sent-badge"
    >
      <CheckCircle className="w-3 h-3" />
      Coached · {when}
    </span>
  );
};
